import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/constants";
import WaitlistForm from "@/components/WaitlistForm";

export default function Footer() {
  return (
    <footer
      className="relative border-t border-white/10 bg-[#0A0B0E]"
      data-testid="main-footer"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img
                src={BRAND.logoTransparent}
                alt="MD Supply Wellness"
                className="h-11 w-auto object-contain"
              />
              <div>
                <p className="font-display text-lg font-semibold text-white">
                  MD Supply Wellness
                </p>
                <p className="text-xs uppercase tracking-[0.2em] text-[#28C0D8]">
                  Bethesda · Maryland
                </p>
              </div>
            </div>
            <p className="text-[15px] leading-relaxed text-[#94A3B8] max-w-md">
              A premium medical & adult-use cannabis wellness dispensary
              bringing lab-tested quality and thoughtful guidance to the
              Bethesda community.
            </p>

            <div className="mt-8 rounded-2xl border border-white/10 bg-[#13161C] p-6">
              <p className="font-display text-lg font-semibold text-white">
                Stay Informed
              </p>
              <p className="text-sm text-[#94A3B8] mt-1">
                Be the first to know when we open our doors.
              </p>
              <div className="mt-4">
                <WaitlistForm source="footer" compact />
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.22em] text-[#64748B] mb-5">
              Explore
            </p>
            <ul className="space-y-3">
              {NAV_LINKS.flatMap((l) =>
                l.children ? l.children : [l]
              ).map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-[15px] text-white/75 hover:text-[#28C0D8] transition"
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.22em] text-[#64748B] mb-5">
              Visit & Contact
            </p>
            <ul className="space-y-4 text-[15px]">
              <li className="flex gap-3 text-white/80">
                <MapPin className="h-5 w-5 shrink-0 text-[#28C0D8]" />
                <span>
                  {BRAND.address.line1}
                  <br />
                  {BRAND.address.line2}
                </span>
              </li>
              <li>
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  className="flex gap-3 text-white/80 hover:text-[#28C0D8] transition"
                >
                  <Phone className="h-5 w-5 shrink-0 text-[#28C0D8]" />
                  {BRAND.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="flex gap-3 text-white/80 hover:text-[#28C0D8] transition break-all"
                >
                  <Mail className="h-5 w-5 shrink-0 text-[#28C0D8]" />
                  {BRAND.email}
                </a>
              </li>
            </ul>

            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.22em] text-[#64748B] mb-3">
                Hours
              </p>
              <ul className="space-y-1.5 text-sm text-white/70">
                {BRAND.hours.map((h) => (
                  <li
                    key={h.day}
                    className="flex justify-between gap-4 border-b border-white/5 pb-1.5"
                  >
                    <span>{h.day}</span>
                    <span className="text-white/90">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-[#64748B]">
            © {new Date().getFullYear()} MD Supply Wellness. All rights
            reserved. For use only by adults 21+ or registered MD medical
            patients.
          </p>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#64748B]">
            Bethesda · Montgomery County · Maryland
          </p>
        </div>
      </div>
    </footer>
  );
}
